/**
 * NotificationsBell — Bell icon with unread badge count and dropdown.
 * 
 * Used in page headers (FundsPage, etc.) to show market alert notifications.
 * Polls for unread count every 60 seconds.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    getNotifications,
    getUnreadNotificationCount,
    markNotificationRead,
    markAllNotificationsRead,
} from '../../services/marketsService';
import '../../styles/FundsPage.css';

const unwrap = (res) => (res && typeof res === 'object' && 'data' in res ? res.data : res);

const POLL_INTERVAL = 60000; // 60 seconds

const NotificationsBell = () => {
    const [unreadCount, setUnreadCount] = useState(0);
    const [showDropdown, setShowDropdown] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(false);
    const dropdownRef = useRef(null);
    const bellRef = useRef(null);

    // Fetch unread count
    const fetchUnreadCount = useCallback(async () => {
        try {
            const raw = await getUnreadNotificationCount();
            const data = unwrap(raw);
            setUnreadCount(data?.unreadCount ?? 0);
        } catch {
            // silently ignore
        }
    }, []);

    // Poll for unread count
    useEffect(() => {
        fetchUnreadCount();
        const interval = setInterval(fetchUnreadCount, POLL_INTERVAL);
        return () => clearInterval(interval);
    }, [fetchUnreadCount]);

    // Fetch full notifications when dropdown opens
    useEffect(() => {
        if (!showDropdown) return;
        setLoading(true);
        getNotifications({ limit: 20 })
            .then((raw) => {
                const data = unwrap(raw);
                setNotifications(Array.isArray(data) ? data : []);
            })
            .catch(() => setNotifications([]))
            .finally(() => setLoading(false));
    }, [showDropdown]);

    // Close dropdown on outside click
    useEffect(() => {
        if (!showDropdown) return;
        const handler = (e) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(e.target) &&
                bellRef.current &&
                !bellRef.current.contains(e.target)
            ) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [showDropdown]);

    const handleMarkRead = async (notificationId) => {
        try {
            await markNotificationRead(notificationId);
            setNotifications((prev) =>
                prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n))
            );
            setUnreadCount((prev) => Math.max(0, prev - 1));
        } catch {
            // ignore
        }
    };

    const handleMarkAllRead = async () => {
        try {
            await markAllNotificationsRead();
            setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
            setUnreadCount(0);
        } catch {
            // ignore
        }
    };

    const formatTime = (dateStr) => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        const now = new Date();
        const diffMs = now - d;
        const diffMins = Math.floor(diffMs / 60000);
        if (diffMins < 1) return 'just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        const diffHrs = Math.floor(diffMins / 60);
        if (diffHrs < 24) return `${diffHrs}h ago`;
        const diffDays = Math.floor(diffHrs / 24);
        return `${diffDays}d ago`;
    };

    return (
        <div className="notifications-bell-container">
            <button
                ref={bellRef}
                className="action-btn notifications-bell-btn"
                onClick={() => setShowDropdown((prev) => !prev)}
                title="Notifications"
            >
                🔔
                {unreadCount > 0 && (
                    <span className="notifications-badge">
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>

            {showDropdown && (
                <div ref={dropdownRef} className="notifications-dropdown">
                    <div className="notifications-dropdown-header">
                        <span className="notifications-dropdown-title">Notifications</span>
                        {unreadCount > 0 && (
                            <button
                                className="notifications-mark-all-btn"
                                onClick={handleMarkAllRead}
                            >
                                Mark all read
                            </button>
                        )}
                    </div>

                    <div className="notifications-dropdown-list">
                        {loading ? (
                            <div className="notifications-loading">Loading…</div>
                        ) : notifications.length === 0 ? (
                            <div className="notifications-empty">No notifications yet</div>
                        ) : (
                            notifications.map((n) => (
                                <div
                                    key={n.id}
                                    className={`notifications-item ${n.read ? '' : 'notifications-item--unread'}`}
                                    onClick={() => !n.read && handleMarkRead(n.id)}
                                >
                                    <div className="notifications-item-msg">{n.message}</div>
                                    <div className="notifications-item-meta">
                                        <span className="notifications-item-symbol">{n.symbol}</span>
                                        <span className="notifications-item-time">{formatTime(n.createdAt)}</span>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationsBell;
